import React, { createContext, useState, useCallback, useContext, ReactNode, useMemo } from 'react';

import api from './../services/api';
import { userHasAnyPermission, userHasPermission } from '../utils/permissions';

interface ISignInCredentials{
	email: string;
	password: string;
}

export interface IUser{
	id: number;
	first_name: string;
	email: string;
	phone: string;
	type: string;
	role: string;
	permissions?: Record<string, boolean>;
}

interface IAuthState{
	user: IUser;
	access_token: string;
	dateToExpires: Date;
}

interface IUpdateUserCredentials{
	first_name: string;
	email: string;
	phone: string;
	password: string;
	password_confirmation: string;
}

interface IUpdateUserCredentialsResponse{
	message: string;
}

interface AuthProviderProps {
	children: ReactNode; 
  }

interface IContext{
	signIn(credentials: ISignInCredentials): Promise<string>;
	signOut(): Promise<void>;
	updateUser(credentials: IUpdateUserCredentials): Promise<IUpdateUserCredentialsResponse>;
    user: IUser | undefined;
    access_token: string;
    dateToExpires: Date;
	hasPermission: (key: string) => boolean;
	hasAnyPermission: (keys: string[]) => boolean;
}

const AuthContext = createContext<IContext>({} as IContext);

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [data, setData] = useState<IAuthState>(() => {
		const access_token = localStorage.getItem('@7Cantos:token');
		const user = localStorage.getItem('@7Cantos:user');
		const dateToExpires = localStorage.getItem('@7Cantos:dateToExpires');

		if(access_token && user && dateToExpires){
			api.defaults.headers.authorization = `Bearer ${access_token}`;

			return {user: JSON.parse(user), access_token, dateToExpires: new Date(dateToExpires)};
		}

		return ({} as IAuthState);
	});

	const signIn = useCallback(async({email, password}: ISignInCredentials): Promise<string> => {
		const responseToken = await api.post('/api/auth/login', {
			email,
			password
		});

		const { access_token, role } = responseToken.data;
		const permsFromLogin = responseToken.data.permissions as Record<string, boolean> | undefined;

		const expiresIn = responseToken.data.expires_in;

		const actualDate = new Date(Date.now());

		const dateToExpires = new Date(actualDate.getFullYear(), actualDate.getMonth(), actualDate.getDate(), actualDate.getHours(), actualDate.getMinutes(), actualDate.getSeconds() + expiresIn);

		api.defaults.headers.authorization = `Bearer ${access_token}`;

		const responseUser = await api.post(
			'/api/auth/me',
			{},
			{
				headers: {
					Authorization: `Bearer ${access_token}`,
				},
			}
		);

		const user: IUser = {
			...responseUser.data,
			role,
			...(permsFromLogin && typeof permsFromLogin === 'object' ? { permissions: permsFromLogin } : {}),
		};

		localStorage.setItem('@7Cantos:token', access_token);
		localStorage.setItem('@7Cantos:user', JSON.stringify(user));
		localStorage.setItem('@7Cantos:dateToExpires', dateToExpires.toString());

		setData({user, access_token, dateToExpires});

		return role;
	}, []);

	const signOut = useCallback(async() => {
		api.defaults.headers.authorization = `Bearer ${data.access_token}`;

		await api.post('/api/auth/logout');

		setData({} as IAuthState);

		localStorage.removeItem('@7Cantos:token');
		localStorage.removeItem('@7Cantos:user');
		localStorage.removeItem('@7Cantos:dateToExpires');
		localStorage.removeItem('@7Cantos:consultants');
	}, [data]);

	const updateUser = useCallback(async({first_name, email, phone, password, password_confirmation}: IUpdateUserCredentials) => {
		const access_token = localStorage.getItem('@7Cantos:token');
		const dateToExpires = localStorage.getItem('@7Cantos:dateToExpires');

		api.defaults.headers.authorization = `Bearer ${access_token}`;

		const responseUpdate = await api.put('/api/client/users', {
			first_name,
			email,
			phone,
			password,
			password_confirmation
		});

		const responseUser = await api.post(
			'/api/auth/me',
			{},
			{
				headers: {
					Authorization: `Bearer ${access_token}`,
				},
			}
		);

		const prev = JSON.parse(localStorage.getItem('@7Cantos:user') || '{}') as IUser;
		const raw = responseUser.data as IUser;
		const user: IUser = {
			...raw,
			role: raw.role ?? prev.role,
			permissions: raw.permissions ?? prev.permissions,
		};

		localStorage.setItem('@7Cantos:user', JSON.stringify(user));

		if(!!access_token && dateToExpires){
			setData({user, access_token, dateToExpires: new Date(dateToExpires)});
		}

		return ({
			message: responseUpdate.data.message
		})
	}, []);

	const hasPermission = useCallback(
		(key: string) => userHasPermission(data.user, key),
		[data.user]
	);

	const hasAnyPermission = useCallback(
		(keys: string[]) => userHasAnyPermission(data.user, keys),
		[data.user]
	);

	const contextValue = useMemo(
		() => ({
			signIn,
			signOut,
			updateUser,
			user: data.user,
			access_token: data.access_token,
			dateToExpires: data.dateToExpires,
			hasPermission,
			hasAnyPermission,
		}),
		[data, signIn, signOut, updateUser, hasPermission, hasAnyPermission]
	);

	return(
		<AuthContext.Provider value={contextValue}>
			{children}
		</AuthContext.Provider>
	);
};

function useAuth():IContext{
	const context = useContext(AuthContext);

	return context;
}

export {AuthProvider, useAuth};
